import React, { useEffect, useState } from "react";
import { useTheme, Button, DialogActions, DialogContent, DialogTitle, Paper, Box } from "@mui/material";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { SimpleFormLabel, SimpleFormTextField } from "components/dialogs/SimpleFormDialog";
import { TextLoader } from "components/apiHandling/TextLoader";
import QueryViewer from "components/queries/QueryViewer";
import APIRequest from "system/APIRequest";
import { Query } from "system/query/Query";
import { QueryInfo } from "system/query/QueryInfo";
import ResponsiveDialog from "components/dialogs/ResponsiveDialog";

interface CopyQueryDialogProps {
  onClose: () => void;
  onComplete: (queryId: string) => void;
  onCopyAndEdit: (query: Query) => void;
  open: boolean;
  query: Query;
}

export function getValidatedCopyName(copyQueryName: string, enqueueSnackbar: any, i18n: any): string | null {
  const trimmedCopyQueryName = copyQueryName.trim();
  if (!trimmedCopyQueryName.length) {
    enqueueSnackbar(`${i18n.t("copy-query-dialog.name")} ${i18n.t("required")}`, { variant: "warning" });
    return null;
  }
  return trimmedCopyQueryName;
}

export default function CopyQueryDialog(props: CopyQueryDialogProps): React.JSX.Element {
  const { i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();

  const [isLoadingQueryInfo, setIsLoadingQueryInfo] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [queryInfo, setQueryInfo] = useState<QueryInfo | null>(null);
  const [copyQueryName, setCopyQueryName] = useState<string>(`${props.query.name} (${i18n.t("copy-noun")})`);

  async function loadQueryInfo() {
    setIsLoadingQueryInfo(true);
    try {
      const response = await APIRequest(`query-info/${props.query.id}`);
      setQueryInfo(new QueryInfo(response));
    } catch (error: any) {
      enqueueSnackbar(`${i18n.t('query-form.error-loading-query-info')}: ${error.message}`, {variant: 'error', persist: true});
      props.onClose();
    } finally {
      setIsLoadingQueryInfo(false);
    }
  }

  function handleCopy() {
    const validatedCopyName = getValidatedCopyName(copyQueryName, enqueueSnackbar, i18n);
    if (!validatedCopyName) {
      return;
    }

    setIsSubmitting(true);

    const newQuery = new Query({
      name: validatedCopyName,
      dataset_id: props.query.dataset_id,
      query: props.query.query,
    });

    APIRequest("obj/query", {}, {
      method: "POST",
      body: JSON.stringify(newQuery)
    }).then((response: any) => {
      enqueueSnackbar(i18n.t("copy-query-dialog.copy-success"), { variant: "success" });
      props.onComplete(response.id);
    }).catch((error: any) => {
      enqueueSnackbar(`${i18n.t('copy-query-dialog.copy-error')}: ${error.message}`, {variant: 'error', persist: true});
    });
  }

  function handleCopyAndEdit() {
    const validatedCopyName = getValidatedCopyName(copyQueryName, enqueueSnackbar, i18n);
    if (!validatedCopyName) {
      return;
    }

    const newQuery = new Query({
      name: validatedCopyName,
      dataset_id: props.query.dataset_id,
      query: props.query.query,
    });

    props.onCopyAndEdit(newQuery);
  }

  useEffect(() => {
    loadQueryInfo();
  }, [props.query.id]);

  return (
    <ResponsiveDialog
      open={props.open}
      onClose={() => props.onClose()}
      maxWidth="md"
    >
      <DialogTitle>{i18n.t("copy-query-dialog.title")}</DialogTitle>
      <DialogContent dividers sx={{ backgroundColor: theme.palette.background.default, p: 1 }}>
        <Paper sx={{ py: 1, px: 2, mb: 1 }}>
          <Box sx={{ display:"grid", gridTemplateColumns: "auto"}}>
            <Box>
              <SimpleFormLabel>{i18n.t("copy-query-dialog.name")}</SimpleFormLabel>
              <SimpleFormTextField
                value={copyQueryName}
                onChange={(e) => setCopyQueryName(e.target.value)} />
            </Box>
          </Box>
        </Paper>
        {isLoadingQueryInfo &&
          <TextLoader />
        }
        {!isLoadingQueryInfo && queryInfo &&
          <Paper sx={{ py: 1, px: 2 }}>
            <SimpleFormLabel>{i18n.t("copy-query-dialog.original-query")}</SimpleFormLabel>
            <QueryViewer query={props.query} queryInfo={queryInfo} />
          </Paper>
        }
      </DialogContent>
      <DialogActions>
        <Button
          color="secondary"
          variant="contained"
          onClick={props.onClose}
        >
          {i18n.t("cancel")}
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={handleCopy}
          disabled={isSubmitting}
        >
          {i18n.t("copy")}
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={handleCopyAndEdit}
          disabled={isSubmitting}
        >
          {i18n.t("copy-query-dialog.copy-and-edit")} 
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  )
}